import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  // Box,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  // ListItemSecondaryAction,
  Alert
} from '@mui/material';
import {
  // Person as PersonIcon,
  // Phone as PhoneIcon,
  Login as LoginIcon,
  PersonAdd as PersonAddIcon,
  ExitToApp as LogoutIcon,
  Close as CloseIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { AppIconButton } from './ui';

interface SessionConflictModalProps {
  isOpen: boolean;
  onClose: () => void;
  onContinueAsGuest: () => void;
  onLoginDifferent: () => void;
  existingSession: {
    type: 'guest' | 'user';
    name?: string;
    phone?: string;
    email?: string;
  } | null;
}

const SessionConflictModal: React.FC<SessionConflictModalProps> = ({
  isOpen,
  onClose,
  onContinueAsGuest,
  onLoginDifferent,
  existingSession
}) => {
  const { clearAllAuthState, signout } = useAuth();
  const navigate = useNavigate();

  const handleContinueAsExisting = () => {
    // Just close the modal and continue with existing session
    onClose();
  };

  const handleLoginAsDifferent = () => {
    // Clear existing session and show login options
    clearAllAuthState();
    onLoginDifferent();
  };

  const handleStartFresh = () => {
    // Clear everything and show guest verification
    clearAllAuthState();
    onContinueAsGuest();
  };

  return (
    <Dialog 
      open={isOpen} 
      onClose={(_, reason) => {
        // Prevent closing by clicking backdrop
        if (reason === 'backdropClick') return;
        onClose();
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        bgcolor: 'primary.main',
        color: 'white'
      }}>
        <Typography variant="h6" fontWeight="600">
          Welcome Back!
        </Typography>
        <AppIconButton
          icon={<CloseIcon />}
          tooltip="Close"
          variant="ghost"
          size="small"
          onClick={onClose}
          edge="end"
          sx={{ color: 'inherit' }}
        />
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {existingSession && (
          <Alert severity="info" sx={{ mb: 3 }}>
            You have an active session as {existingSession.type === 'guest' ? 'Guest' : 'User'}
            {existingSession.name && ` (${existingSession.name})`}
          </Alert>
        )}

        <Typography variant="body1" color="text.secondary" gutterBottom>
          How would you like to continue?
        </Typography>

        <List sx={{ mt: 2 }}>
          {/* Continue with existing session */}
          <ListItem 
            button 
            onClick={handleContinueAsExisting}
            sx={{ 
              border: 1, 
              borderColor: 'divider', 
              borderRadius: 1, 
              mb: 1,
              '&:hover': { bgcolor: 'action.hover' }
            }}
          >
            <ListItemAvatar>
              <Avatar sx={{ bgcolor: 'success.light', color: 'success.main' }}>
                <CheckIcon />
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={`Continue as ${existingSession?.name || 'Current User'}`}
              secondary={
                existingSession?.type === 'guest' 
                  ? `Guest • ${existingSession?.phone || 'Phone verified'}`
                  : `Logged in user • ${existingSession?.email || ''}`
              }
            />
          </ListItem>

          {/* Login as different user */}
          <ListItem 
            button 
            onClick={handleLoginAsDifferent}
            sx={{ 
              border: 1, 
              borderColor: 'divider', 
              borderRadius: 1, 
              mb: 1,
              '&:hover': { bgcolor: 'action.hover' }
            }}
          >
            <ListItemAvatar>
              <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.main' }}>
                <LoginIcon />
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary="Login with Different Account"
              secondary="Use your username/password or social login"
            />
          </ListItem>

          {/* Start fresh as new guest */}
          <ListItem 
            button 
            onClick={handleStartFresh}
            sx={{ 
              border: 1, 
              borderColor: 'divider', 
              borderRadius: 1,
              '&:hover': { bgcolor: 'action.hover' }
            }}
          >
            <ListItemAvatar>
              <Avatar sx={{ bgcolor: 'warning.light', color: 'warning.main' }}>
                <PersonAddIcon />
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary="Continue as New Guest"
              secondary="Start fresh with phone verification"
            />
          </ListItem>
        </List>

        <Divider sx={{ my: 2 }} />

        <Typography variant="caption" color="text.secondary" display="block" textAlign="center">
          Your previous order history will be maintained based on your choice
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          variant="text"
          color="error"
          startIcon={<LogoutIcon />}
          onClick={() => {
            // Use centralized signout so users see unified "Logging out..." toast
            signout();
          }}
        >
          Logout Completely
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SessionConflictModal;